// Route Code Edited 09/15/2026
/**
 * Route — Replace the PC EQ Value Prop Accordion header text with a quote-integrated sentence.
 *          APPROACH 3: screen-reader-accessible real DOM node (not a CSS pseudo-element).
 *
 * GOAL
 *   Native PC EQ header text: "Your order is protected."
 *   Desired:                  "Your order is protected for $XX.XX."  (visible AND announced)
 *
 * CONTEXT
 *   The "Preferred Checkout Embedded Quote" (PC EQ) widget renders the premium in the cloned
 *   checkout button (stacked-premium preset) but NOT in the Value Prop Accordion header.
 *   Employee Tools cannot configure the quote into both places, and widget code changes are
 *   out of scope, so this theme script rewrites the header presentation.
 *
 * APPROACH 3 — why a real node
 *   Approach 1 rendered the sentence in a CSS ::after pseudo-element, which screen readers
 *   announce inconsistently. This version instead:
 *     1. Hides the native "Your order is protected." from BOTH sight and assistive tech:
 *        a visually-hidden CSS rule (keyed on a foreign data-* attribute) removes it from
 *        layout, and aria-hidden="true" removes it from the accessibility tree. Neither
 *        touches the React-owned text node.
 *     2. Injects a REAL <span> sibling carrying the full sentence, so it is rendered normally
 *        AND announced by screen readers. Its font is copied from the native element for a
 *        faithful visual match.
 *   React does not manage attributes/elements it never rendered, so the aria-hidden attribute
 *   and the injected span survive React re-renders; the observer re-injects if a full header
 *   re-mount ever removes the span.
 *
 * RESIDUAL RISK (inherent to Approach 3 — accept knowingly)
 *   The span is injected into a React-managed container. Validated live on the Bogg Bag draft
 *   theme: it renders at the native size, is announced, updates on re-quote, and survives
 *   repeated rapid cart changes with NO console/reconciliation errors and no duplication.
 *   NOT exercised live: accordion expand/collapse, which changes the header id
 *   (…-collapsed <-> …-expanded) and likely replaces the header subtree with the span present.
 *   The observer re-injects afterward; crash risk is low because the widget replaces the
 *   subtree wholesale rather than diffing our sibling, but it is not zero. Test expand/collapse
 *   on staging with the console open. If any reconciliation error appears, fall back to
 *   Approach 1 (pseudo-element), which cannot crash React.
 *
 * UPDATE / RE-SYNC NOTE
 *   On a cart change (quantity, or add-from-upsell) the header PERSISTS and Route re-quotes
 *   ASYNCHRONOUSLY. A single sync fired during the drawer re-render reads the old premium and
 *   nothing re-runs once the new one lands. Fix: after any cart-region mutation, re-run sync a
 *   couple of times once activity settles (setTimeout — runs even when the tab is backgrounded,
 *   unlike requestAnimationFrame). We also cache the last good premium and never strip the
 *   override on a transient empty read. Validated live: line updated $7.15 -> $7.75 -> $9.38
 *   with no manual intervention.
 *
 * SAFE DEGRADATION
 *   READS Route's DOM; WRITES one <style>, plus (per header) an aria-hidden + data attribute
 *   and one injected span. If a selector stops matching, nothing is hidden and no span is
 *   injected — the native "Your order is protected." renders unchanged. Removing the <script>
 *   tag and this asset fully reverts the change (injected spans vanish on reload).
 *
 * KNOWN ASSUMPTIONS / FRAGILITY (current Route internals — may change without notice)
 *   - Header element class:         .pw-coverage-description   (id is state-suffixed; do NOT target it)
 *   - Cloned button premium source: [data-route-cloned-button] .route-premium-text-stacked
 *   - Premium is the first whitespace-delimited token, leading "+" stripped:
 *       "+$19.13 Package Protection" -> "$19.13"
 *
 * INSTALLATION
 *   Upload as assets/route-custom-widget-override.js (or your chosen asset name), then add at
 *   the bottom of layout/theme.liquid (immediately before </body>) — match the src to the name:
 *
 *   <!-- Route Code Edited 09/15/2026 -->
 *   <script src="{{ 'route-custom-widget-override-a11y.js' | asset_url }}" defer></script>
 *   <!-- End Route Code -->
 */
(function routeEqHeaderQuoteA11y() {
  if (window.__routeEqHeaderQuoteInit) return;
  window.__routeEqHeaderQuoteInit = true;

  // ─── Selectors / config (update if Route renames internals) ──────────────────
  const HEADER_SELECTOR = '.pw-coverage-description';           // stable class, NOT the state-suffixed id
  const PREMIUM_SELECTOR = '[data-route-cloned-button] .route-premium-text-stacked';
  const LINE_CLASS = 'route-ie-quote-line';                     // our injected accessible node
  const HIDE_ATTR = 'data-route-ie-hidden';                     // marks the native element for hiding
  const LEAD = 'Your order is protected for ';                  // sentence = LEAD + <price> + TAIL
  const TAIL = '.';
  const TRAILING_DELAYS = [600, 1300];                          // re-sync after cart activity settles (ms)
  // Visual props copied from the native element onto the injected span for a faithful match.
  const COPY_PROPS = ['fontFamily', 'fontWeight', 'fontStyle', 'color', 'lineHeight', 'letterSpacing', 'textAlign'];
  // ─────────────────────────────────────────────────────────────────────────────

  // One-time style: visually-hide (not font-size:0) the native element when marked. This keeps
  // it out of layout without zeroing font metrics, so the copied size on our span stays correct.
  const style = document.createElement('style');
  style.setAttribute('data-route-ie-style', 'true');
  style.textContent =
    HEADER_SELECTOR + '[' + HIDE_ATTR + ']{' +
      'position:absolute!important;width:1px!important;height:1px!important;' +
      'padding:0!important;margin:-1px!important;overflow:hidden!important;' +
      'clip:rect(0 0 0 0)!important;clip-path:inset(50%)!important;' +
      'white-space:nowrap!important;border:0!important;}';
  document.head.appendChild(style);

  let lastPremium = ''; // remember the most recent valid price across transient empty reads

  function readPremium() {
    const el = document.querySelector(PREMIUM_SELECTOR);
    if (!el) return '';
    const text = (el.textContent || '').trim();
    if (!text) return '';
    return text.split(/\s+/)[0].replace(/^\+/, ''); // "+$19.13 Package Protection" -> "$19.13"
  }

  function ensureLine(cover, premium) {
    const parent = cover.parentElement;
    if (!parent) return;
    let line = parent.querySelector(':scope > .' + LINE_CLASS);
    if (!line) {
      // Copy visual styling from the native element BEFORE it is hidden.
      const cs = getComputedStyle(cover);
      line = document.createElement('span');
      line.className = LINE_CLASS;
      COPY_PROPS.forEach(function (p) { line.style[p] = cs[p]; });
      const size = parseFloat(cs.fontSize) > 0 ? cs.fontSize : ''; // guard against a zeroed read
      if (size) line.style.fontSize = size;
      cover.insertAdjacentElement('afterend', line); // trailing sibling — lowest reconciliation risk
    }
    const text = LEAD + premium + TAIL;
    if (line.textContent !== text) line.textContent = text;
    // Hide the native text from sight and from assistive tech (without touching React's node).
    if (cover.getAttribute(HIDE_ATTR) !== 'true') cover.setAttribute(HIDE_ATTR, 'true');
    if (cover.getAttribute('aria-hidden') !== 'true') cover.setAttribute('aria-hidden', 'true');
  }

  function sync() {
    const covers = document.querySelectorAll(HEADER_SELECTOR);
    if (!covers.length) return;                  // no widget/header present -> nothing to do
    const premium = readPremium();
    if (premium) lastPremium = premium;          // update cache only on a valid read
    if (!lastPremium) return;                     // never had a price yet -> wait
    covers.forEach(function (c) { ensureLine(c, lastPremium); });
  }

  // Immediate (coalesced) apply. setTimeout, not rAF, so it runs even when the tab is hidden.
  let scheduled = false;
  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () { scheduled = false; sync(); }, 16);
  }

  // Trailing re-sync burst: catches the async re-quote that lands after the drawer re-renders.
  // Reset on each call so it fires relative to the LAST mutation.
  let trailingTimers = [];
  function armTrailing() {
    trailingTimers.forEach(clearTimeout);
    trailingTimers = TRAILING_DELAYS.map(function (d) { return setTimeout(sync, d); });
  }

  function onActivity() { scheduleSync(); armTrailing(); }

  const observer = new MutationObserver(onActivity);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });

  ['cart:change', 'cart:refresh', 'cart-drawer:refreshed', 'cart:updated'].forEach(function (evt) {
    document.addEventListener(evt, onActivity);
  });

  window.addEventListener('pagehide', function () {
    observer.disconnect();
    trailingTimers.forEach(clearTimeout);
  });

  onActivity(); // initial pass
})();
// End Route Code